// Browser-side part lookup for Quick List.
// Used when the connected server has no /admin/quicklist API (e.g. before the
// backend is deployed): the dashboard then calls Mouser directly with a key
// saved in this browser only. Mouser's API allows these cross-origin calls.
import api from './api';

const MOUSER_URL = 'https://api.mouser.com/api/v1/search/partnumber';
const MOUSER_BATCH_SIZE = 10; // Mouser accepts up to 10 part numbers per call
const MOUSER_MIN_GAP_MS = 2200; // Mouser allows 30 calls per minute
const KEY_STORAGE = 'quickList:mouserKey';

// Attributes that describe how Mouser sells the part rather than the part itself.
// Packaging is kept: it is mapped onto the catalog's names (Tape & Reel (TR)...) later
const SKIPPED_ATTRIBUTES = new Set(['factory pack quantity', 'unit weight', 'standard pack qty']);

// Mouser's generic images are named after the package (QSOP_16_t.jpg, SOT_23_5_t.jpg).
const PACKAGE_PREFIX = /^(SOT|SOIC|SO|QSOP|SSOP|TSSOP|MSOP|VSSOP|TSOP|QFN|VQFN|WQFN|DFN|UDFN|TDFN|SON|WSON|VSON|QFP|LQFP|TQFP|BGA|FBGA|LGA|DIP|PDIP|SIP|TO|DPAK|D2PAK|SOD|SC|SMA|SMB|SMC|MELF|PLCC|SC70|UQFN|XQFN|WLCSP|CSP)$/i;

// The dashboard calls localStorage.clear() on logout and on an expired session,
// so the key is also kept in a cookie (which that does not touch). Connecting
// once is then enough on this browser. REACT_APP_MOUSER_API_KEY, if set at
// build time, is used as a fallback for every browser.
const KEY_COOKIE = 'ql_mouser_key';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const readCookie = () => {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${KEY_COOKIE}=`));
    return hit ? decodeURIComponent(hit.slice(KEY_COOKIE.length + 1)) : '';
  } catch (e) {
    return '';
  }
};

const writeCookie = (value) => {
  try {
    document.cookie = value
      ? `${KEY_COOKIE}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Strict`
      : `${KEY_COOKIE}=; max-age=0; path=/; SameSite=Strict`;
  } catch (e) {
    // Cookies blocked: localStorage is still used.
  }
};

export const getBrowserMouserKey = () => {
  let stored = '';
  try {
    stored = localStorage.getItem(KEY_STORAGE) || '';
  } catch (e) {
    stored = '';
  }
  const key = stored || readCookie() || process.env.REACT_APP_MOUSER_API_KEY || '';
  // Put it back into localStorage after a logout cleared it.
  if (key && !stored) {
    try {
      localStorage.setItem(KEY_STORAGE, key);
    } catch (e) {
      // ignore
    }
  }
  return key;
};

export const setBrowserMouserKey = (key) => {
  writeCookie(key);
  try {
    if (key) localStorage.setItem(KEY_STORAGE, key);
    else localStorage.removeItem(KEY_STORAGE);
  } catch (e) {
    // Storage blocked: the cookie still keeps the key.
  }
};

export const normalizePartNumber = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[\s\-_.]/g, '')
    .trim();

export const packageFromImage = (imagePath) => {
  const file = String(imagePath || '').split('/').pop() || '';
  const match = file.match(/^([A-Za-z0-9]+(?:_[A-Za-z0-9]+)*)_t\.(?:jpe?g|png|gif)$/i);
  if (!match) return '';
  const parts = match[1].split('_');
  if (!PACKAGE_PREFIX.test(parts[0])) return '';
  return parts.join('-').toUpperCase();
};

const normalizeRohs = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return /not|non|no\b/i.test(text) ? 'NO' : 'YES';
};

const absoluteUrl = (url, base) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  return base ? `${base}/${value.replace(/^\//, '')}` : value;
};

export const emptyResult = (partNumber) => ({
  partNumber,
  found: false,
  source: null,
  productNumber: partNumber,
  manufacturer: '',
  description: '',
  datasheetUrl: '',
  images: [],
  category: '',
  categoryPath: [],
  attributes: {},
  rohs: '',
  leadTime: '',
  supplierUrl: '',
  standardPackQty: '',
  hsCode: '',
  existingProductId: null,
});

/** Same shape as the backend mapper (services/partLookup/mouser.js). */
export const mapMouserPart = (partNumber, part) => {
  const attributes = {};
  const push = (name, value) => {
    const key = String(name || '').trim();
    const val = String(value ?? '').trim();
    if (!key || !val || val === '-') return;
    if (!attributes[key]) attributes[key] = [];
    if (!attributes[key].includes(val)) attributes[key].push(val);
  };

  (part.ProductAttributes || []).forEach((attr) => {
    if (SKIPPED_ATTRIBUTES.has(String(attr?.AttributeName || '').trim().toLowerCase())) return;
    push(attr?.AttributeName, attr?.AttributeValue);
  });

  const image = absoluteUrl(part.ImagePath, 'https://www.mouser.com');
  const pkg = packageFromImage(part.ImagePath);
  if (pkg && !attributes['Package / Case']) push('Package / Case', pkg);

  const spq = (part.ProductAttributes || []).find((a) => /standard pack qty/i.test(a?.AttributeName || ''));
  // International HS code (first 6 digits are the same in every country).
  const hts = (part.ProductCompliance || []).find((c) => /^(TARIC|CNHTS|USHTS)$/i.test(c?.ComplianceName || ''));

  return {
    ...emptyResult(partNumber),
    found: true,
    source: 'mouser',
    productNumber: String(part.ManufacturerPartNumber || partNumber).trim(),
    manufacturer: String(part.Manufacturer || '').trim(),
    description: String(part.Description || '').trim(),
    datasheetUrl: absoluteUrl(part.DataSheetUrl),
    images: image ? [image] : [],
    category: String(part.Category || '').trim(),
    categoryPath: part.Category ? [String(part.Category).trim()] : [],
    attributes,
    rohs: normalizeRohs(part.ROHSStatus),
    leadTime: String(part.LeadTime || '').trim(),
    supplierUrl: absoluteUrl(part.ProductDetailUrl),
    standardPackQty: spq?.AttributeValue ? String(spq.AttributeValue).trim() : '',
    hsCode: hts?.ComplianceValue ? String(hts.ComplianceValue).replace(/\D/g, '').slice(0, 6) : '',
  };
};

const scorePart = (part) =>
  (part.ImagePath ? 2 : 0) + (part.DataSheetUrl ? 2 : 0) + (part.ProductAttributes?.length ? 1 : 0) + (part.Description ? 1 : 0);

const friendlyMouserError = (error) => {
  if (error?.PropertyName === 'API Key' || /invalid unique identifier/i.test(error?.Message || '')) {
    return 'Mouser rejected the API key. Use the Search API key (not the Cart/Order API key).';
  }
  if (/too many|exceed|limit/i.test(error?.Message || '')) {
    return 'Mouser limit reached (30 searches per minute / 1000 per day). Wait a minute and retry the failed rows.';
  }
  return `Mouser: ${error?.Message || 'unknown error'}`;
};

/** Looks up at most 10 part numbers. Returns Map(normalizedPart -> result) of found parts. */
export const searchMouserBrowser = async (partNumbers, apiKey) => {
  const found = new Map();
  if (!partNumbers.length) return found;

  let response;
  try {
    response = await fetch(`${MOUSER_URL}?apiKey=${encodeURIComponent(apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        SearchByPartRequest: { mouserPartNumber: partNumbers.join('|'), partSearchOptions: 'Exact' },
      }),
    });
  } catch (e) {
    throw new Error('Could not reach Mouser. Check your internet connection.');
  }

  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }

  if (Array.isArray(body?.Errors) && body.Errors.length > 0) throw new Error(friendlyMouserError(body.Errors[0]));
  if (!response.ok) throw new Error(`Mouser request failed (HTTP ${response.status})`);

  const parts = body?.SearchResults?.Parts || [];
  partNumbers.forEach((partNumber) => {
    const key = normalizePartNumber(partNumber);
    const matches = parts.filter(
      (p) => normalizePartNumber(p.ManufacturerPartNumber) === key || normalizePartNumber(p.MouserPartNumber) === key
    );
    if (!matches.length) return;
    const best = [...matches].sort((a, b) => scorePart(b) - scorePart(a))[0];
    found.set(key, mapMouserPart(partNumber, best));
  });

  return found;
};

// Bulk save updates a product when the part number already exists, so mark those.
const findExistingProductId = async (partNumber) => {
  try {
    const res = await api.getAllProduct(`?search=${encodeURIComponent(partNumber)}&page=1&limit=10`);
    const list = res?.data?.data || [];
    const wanted = String(partNumber).trim().toUpperCase();
    const hit = list.find((p) =>
      [...(p?.productOverview?.productNumber || []), ...(p?.productOverview?.ElectrowizCode || [])].some(
        (pn) => String(pn).trim().toUpperCase() === wanted
      )
    );
    return hit?._id || null;
  } catch (e) {
    return null;
  }
};

const isSupplierHosted = (url) => {
  try {
    return /(^|\.)(mouser|digikey)\.[a-z.]+$/.test(new URL(url).hostname.toLowerCase());
  } catch (e) {
    return true;
  }
};

/**
 * Picks an image from our own catalog for a category: the first product in
 * that category (or its children) that has an image we host ourselves.
 */
export const findCatalogImage = async (categoryId) => {
  try {
    const res = await api.getAllProduct(`?categoryId=${encodeURIComponent(categoryId)}&page=1&limit=25`);
    const list = res?.data?.data || [];
    for (const product of list) {
      const candidates = [...(product?.assets?.featuredImage || []), ...(product?.assets?.image || [])];
      const url = candidates.find((u) => u && /^https?:\/\//i.test(u) && !isSupplierHosted(u));
      if (url) return url;
    }
  } catch (e) {
    // No image is fine; the row simply stays without one.
  }
  return '';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let lastMouserCallAt = 0;

/**
 * Browser equivalent of POST /admin/quicklist/lookup for up to 10 parts.
 * Returns { results, warnings } in the same shape as the server.
 */
export const lookupPartsInBrowser = async (partNumbers, apiKey) => {
  const warnings = [];
  let found = new Map();

  // Respect Mouser's rate limit between consecutive calls.
  const wait = lastMouserCallAt + MOUSER_MIN_GAP_MS - Date.now();
  if (wait > 0) await sleep(wait);
  lastMouserCallAt = Date.now();

  try {
    found = await searchMouserBrowser(partNumbers.slice(0, MOUSER_BATCH_SIZE), apiKey);
  } catch (e) {
    warnings.push(e.message);
    return { results: null, warnings };
  }

  const results = await Promise.all(
    partNumbers.map(async (pn) => {
      const data = found.get(normalizePartNumber(pn));
      const result = data ? { ...data, partNumber: pn } : emptyResult(pn);
      const existingProductId =
        (await findExistingProductId(result.productNumber)) ||
        (result.productNumber !== pn ? await findExistingProductId(pn) : null);
      return { ...result, existingProductId };
    })
  );

  return { results, warnings };
};

export const BROWSER_LOOKUP_BATCH_SIZE = MOUSER_BATCH_SIZE;
